import React, { useContext, useEffect, useReducer } from 'react';
import clsx from 'clsx';
import { useQuery, useLazyQuery } from '@apollo/client';
import DatePicker from 'react-datepicker';
import { FetchPolicy, DataSort } from 'utils/enum/Core';
import { useTheme } from '@material-ui/core/styles';
import DateUtils from 'lib/DateUtils';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
    makeStyles,
    Container,
    Button,
} from '@material-ui/core';
import { Form, Row } from 'react-bootstrap';
import Select from 'components/widgets/Select';
import ButtonStyles from 'styles/theme/Button';
import { CachedIcon } from 'components/icons';
import ModalUtils from 'utils/ModalUtils';
import UserContext from 'components/context/UserContext';
import ReportHeader from 'components/reports/ReportHeader';
import CalendarContainer from 'components/widgets/form/CalendarContainer';
import LotQuery from 'services/graphQL/query/LotQuery';
import ReportQuery from 'services/graphQL/query/ReportQuery';
import { VendorType } from 'utils/enum/Vendors';
import VendorQuery from 'services/graphQL/query/VendorQuery';

const calculatedStartMonth = new Date(DateUtils.subtract(Date.now(), 1, 'months'));
calculatedStartMonth.setDate(1);

const INIT_STATE = {
    securedURL: '',
    vendorList: [],
    selectedLot: -1,
    lotsAvailable: [],
    taxAssessorSelected: -1,
    appraisalDistrictSelected: -1,
    selectedDate: calculatedStartMonth,
};

const ACTION_TYPES = {
    SET_AVAILABLE_LOTS: 'setAvailableLots',
    CHANGE_FIELD: 'changeField',
    SET_VENDOR_LIST: 'setVendorList',
};

const reducer = (state, action = {}) => {
    switch (action.type) {
    case ACTION_TYPES.SET_VENDOR_LIST:
        const { vendorList } = action.payload;

        return {
            ...state,
            vendorList,
        };
    case ACTION_TYPES.SET_AVAILABLE_LOTS:
        const { lots, selected } = action.payload;

        return {
            ...state,
            selectedLot: selected,
            lotsAvailable: lots,
        };
    case ACTION_TYPES.CHANGE_FIELD:
        const { name, value } = action.payload;

        return {
            ...state,
            [name]: value,
        };
    default:
        return state;
    }
};

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyle = makeStyles((theme) => ({
    container: {
        padding: '15px',
    },
    noPadding: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
    },
    subLabel: {
        fontSize: '13px',
        marginLeft: '5px',
        marginRight: '10px',
        marginTop: '8px',
    },
    group: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            justifyContent: 'center',
        },
    },
    input: {
        fontSize: '14px',
        resize: 'none',
        width: '150px',
    },
    field: {
        fontSize: '14px',
        backgroundColor: `${theme.palette.background.white} !important`,
    },
    '@global': {
        '.css-26l3qy-menu div': {
            fontSize: '14px',
            lineHeight: '1.4',
        },
    },
    goButton: {
        [theme.breakpoints.down('sm')]: {
            marginLeft: '30px',
        },
    },
    box: {
        padding: '20px',
        '& > div': {
            paddingRight: '200px',
            [theme.breakpoints.down('sm')]: {
                paddingRight: 0,
                '& > div': {
                    margin: 0,
                },
            },
        },
    },
    ranOn: {
        marginTop: '30px',
        display: 'flex',
        width: '100%',
        '& > div': {
            marginRight: '20px',
        },
        '& > button': {
            height: '31px',
        },
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-end',
            '& > div': {
                marginBottom: '10px !important',
            },
            '& > button': {
                width: '100px',
                marginLeft: 0,
                marginRight: '20px',
            },
        },
    },
    links: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: '30px',
        width: '100%',
        lineHeight: 1.5,
        fontSize: '13px',
        marginLeft: '-10px',
        '& > a': {
            color: theme.palette.link,
        },
        '& > div': {
            textDecoration: 'underline',
        },
    },
    disabledLink: {
        pointerEvents: 'none',
        color: `${theme.palette.grey[600]} !important`,
    },
    headers: {
        fontSize: '13px',
        marginBottom: '15px',
        marginLeft: '-1px',
        textDecoration: 'none',
    },
    datePicker: {
        maxWidth: 100,
    },
    iframe: {
        width: '100%',
        height: 'calc(100vh - 80px)',
    },
}));

const InventoryTax = () => {
    const classes = { ...useStyle(), ...buttonStyles() };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { userInformation } = useContext(UserContext);
    const [state, dispatch] = useReducer(reducer, INIT_STATE);

    const {
        securedURL,
        vendorList,
        selectedLot,
        lotsAvailable,
        selectedDate,
        taxAssessorSelected,
        appraisalDistrictSelected,
    } = state;

    const {
        data: lotsData,
        loading: loadingLots,
        error: errorLoadingLots,
    } = useQuery(LotQuery.GET_LOTS, {
        variables: {
            filter: {
                active: true,
            },
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const {
        data: vendorsData,
        loading: loadingVendors,
        error: vendorsError,
    } = useQuery(VendorQuery.GET_VENDOR_LIST, {
        variables: {
            filter: {
                vendorType: [VendorType.APPRAISAL_DISTRICT, VendorType.TAX_ASSESSOR_COLLECTOR],
                active: true,
            },
            sort: {
                dir: DataSort.ASC,
                field: 'vendorName',
            },
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [pullReport, { loading }] = useLazyQuery(ReportQuery.GET_INVENTORY_TAX_REPORT, {
        onCompleted: (response) => {
            const data = response?.getInventoryTaxReport;
            dispatch({
                payload: {
                    name: 'securedURL',
                    value: data,
                },
                type: ACTION_TYPES.CHANGE_FIELD,
            });
        },
        onError: (err) => {
            ModalUtils.errorMessage([err]);
        },
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    useEffect(() => {
        if (errorLoadingLots) {
            ModalUtils.errorMessage(errorLoadingLots?.graphQLErrors);
        }

        if (!loadingLots) {
            const lots = lotsData?.lotList;
            if (lots) {
                dispatch({
                    type: ACTION_TYPES.SET_AVAILABLE_LOTS,
                    payload: {
                        selected: selectedLot,
                        lots: lots.map((lot) => ({
                            value: lot.lotId,
                            label: lot.lotName,
                        })),
                    },
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingLots, errorLoadingLots]);

    useEffect(() => {
        if (userInformation && lotsAvailable.length > 0) {
            const defaultLot = userInformation?.defaultLot;

            dispatch({
                type: ACTION_TYPES.SET_AVAILABLE_LOTS,
                payload: {
                    selected: lotsAvailable.find((lot) => lot.label?.toLowerCase() === defaultLot?.toLowerCase())?.value || -1,
                    lots: lotsAvailable,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInformation, lotsAvailable]);

    useEffect(() => {
        if (vendorsError) {
            ModalUtils.errorMessage(vendorsError?.graphQLErrors);
        }

        if (!loadingVendors && vendorsData) {
            dispatch({
                payload: {
                    vendorList: vendorsData.getVendorList,
                },
                type: ACTION_TYPES.SET_VENDOR_LIST,
            });
        }
    }, [vendorsData, loadingVendors, vendorsError]);

    const onChange = (name, value) => {
        dispatch({
            type: ACTION_TYPES.CHANGE_FIELD,
            payload: {
                name,
                value,
            },
        });
    };

    return (
        <Container
            maxWidth={false}
            disableGutters
            className={clsx({ [classes.noPadding]: isMobile },
                { [classes.container]: !isMobile })}
        >
            <ReportHeader
                titleReport="Inventory Tax"
            >
                <Form.Group as={Row} className={classes.group}>
                    <Form.Label className={classes.subLabel}>Appraisal District:</Form.Label>
                    <Select
                        nowrap
                        size="sm"
                        className={classes.input}
                        name="appraisalDistrictSelected"
                        value={appraisalDistrictSelected}
                        onChange={(name, value) => onChange(name, value)}
                        options={
                            vendorList
                                .filter((vendor) => vendor.vendorType === VendorType.APPRAISAL_DISTRICT)
                                .map((vendor) => ({ label: vendor.vendorName, value: vendor.vendorId }))
                        }
                    />
                </Form.Group>
                <Form.Group as={Row} className={classes.group}>
                    <Form.Label className={classes.subLabel}>Tax Assessor:</Form.Label>
                    <Select
                        nowrap
                        size="sm"
                        className={classes.input}
                        name="taxAssessorSelected"
                        value={taxAssessorSelected}
                        onChange={(name, value) => onChange(name, value)}
                        options={
                            vendorList
                                .filter((vendor) => vendor.vendorType === VendorType.TAX_ASSESSOR_COLLECTOR)
                                .map((vendor) => ({ label: vendor.vendorName, value: vendor.vendorId }))
                        }
                    />
                </Form.Group>
                <Form.Group as={Row} className={classes.group}>
                    <Form.Label className={classes.subLabel}>Lot Name:</Form.Label>
                    <Select
                        nowrap
                        size="sm"
                        className={classes.input}
                        name="selectedLot"
                        onChange={(name, value) => onChange(name, value)}
                        value={selectedLot}
                        options={lotsAvailable}
                    />
                </Form.Group>
                <Form.Group as={Row} className={classes.group}>
                    <Form.Label className={classes.subLabel}>Month / Year:</Form.Label>
                    <DatePicker
                        size="sm"
                        showMonthYearPicker
                        showFullMonthYearPicker
                        dateFormat="MM/yyyy"
                        selected={selectedDate}
                        className={clsx('form-control form-control-sm', classes.datePicker)}
                        popperContainer={CalendarContainer}
                        maxDate={new Date()}
                        onChange={(date) => onChange('selectedDate', date)}
                    />
                </Form.Group>
                <Button
                    className={classes.goButton}
                    variant="outlined"
                    startIcon={<CachedIcon />}
                    disabled={selectedLot < 0 || !selectedDate || loading}
                    size="small"
                    onClick={() => {
                        pullReport({
                            variables: {
                                lotId: selectedLot,
                                reportDate: DateUtils.format(selectedDate),
                                taxAssessorCollectorId: taxAssessorSelected,
                                appraisalDistrictId: appraisalDistrictSelected,
                            },
                        });
                    }}
                >
                    Preview Report
                </Button>
            </ReportHeader>
            {securedURL && (
                <iframe
                    title="Preview"
                    frameBorder="0"
                    allowFullScreen
                    src={securedURL}
                    className={classes.iframe}
                />
            )}
        </Container>
    );
};

export default InventoryTax;
